/**
 * AdminPointsCalculator - Freie Punkteauswahl mit Staffelrabatt für die Verkaufsseite.
 * Wie der Kundenshop-Rechner (Slider/Raster), plus optionale Extras:
 * Bonus-Punkte (+%) oder Zusatz-Rabatt (−%).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Minus, Plus, Gift, Percent, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const AdminPointsCalculator = ({ API, onSelect, customerId }) => {
  const [points, setPoints] = useState(20);
  const [inputValue, setInputValue] = useState('20');
  const [calc, setCalc] = useState(null);
  const [extraType, setExtraType] = useState('bonus'); // 'bonus' | 'discount'
  const [extraValue, setExtraValue] = useState('');

  const calculate = useCallback(async (pts) => {
    if (pts < 1) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/sales/volume-calculator?points=${pts}${customerId ? `&customer_id=${customerId}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data.error) setCalc(res.data);
    } catch (e) {
      console.error('Volume calc error:', e);
    }
  }, [API, customerId]);

  useEffect(() => {
    const timer = setTimeout(() => calculate(points), 300);
    return () => clearTimeout(timer);
  }, [points, calculate]);

  const updatePoints = (val) => {
    const p = Math.max(1, Math.min(9999, Number(val) || 1));
    setPoints(p);
    setInputValue(String(p));
  };

  if (!calc) return null;

  const sliderMax = (() => {
    const tiers = calc.all_tiers || [];
    if (tiers.length === 0) return 100;
    const sorted = [...tiers].sort((a, b) => (a.min_points || 0) - (b.min_points || 0));
    const top = sorted[sorted.length - 1];
    return Number(top.max_points ?? top.min_points) || 100;
  })();

  const extraNum = Math.max(0, parseFloat(extraValue) || 0);
  const pct = Math.min(100, extraNum);
  const isBonusMode = calc.pricing_mode === 'bonus';
  const tierBonus = isBonusMode ? (calc.bonus_points || 0) : 0;
  const manualBonus = extraType === 'bonus' ? Math.round(extraNum) : 0;
  const bonusPoints = manualBonus + tierBonus;
  const extraDiscountAmount = extraType === 'discount' ? Math.round(calc.final_price * pct / 100 * 100) / 100 : 0;
  const finalPrice = Math.round((calc.final_price - extraDiscountAmount) * 100) / 100;
  const totalPoints = points + bonusPoints;

  const handleContinue = () => {
    const noteParts = [`Freie Punkteanzahl: ${points} Punkte`];
    if (isBonusMode && tierBonus > 0) noteParts.push(`Staffel-Bonus ${calc.tier_label}: +${tierBonus} Punkte gratis (${calc.discount_percent}%)`);
    if (!isBonusMode && calc.discount_percent > 0) noteParts.push(`Staffelrabatt ${calc.tier_label} -${calc.discount_percent}%`);
    if (manualBonus > 0) noteParts.push(`Bonus: +${manualBonus} Punkte geschenkt`);
    if (extraDiscountAmount > 0) noteParts.push(`Zusatz-Rabatt -${pct}% (-CHF ${extraDiscountAmount.toFixed(2)})`);
    onSelect({
      custom_points: true,
      name: bonusPoints > 0 ? `${points} Punkte + ${bonusPoints} Bonus 🎁` : `${points} Punkte (individuell)`,
      points_amount: totalPoints,
      bonus_points: bonusPoints,
      price: finalPrice,
      sale_note: noteParts.join(' | '),
    });
  };

  return (
    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/40" data-testid="admin-points-calculator">
      <h3 className="font-bold mb-1">🎯 Freie Punkteanzahl</h3>
      <p className="text-xs text-gray-500 mb-3">Staffelrabatt wird automatisch angewendet — wie im Kundenshop.</p>

      {/* Slider + Input */}
      <div className="bg-white rounded-lg border p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Anzahl Punkte</span>
          <Input
            type="number"
            min="1"
            max="9999"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              const v = Number(e.target.value);
              if (v >= 1) setPoints(v);
            }}
            onBlur={() => updatePoints(inputValue)}
            className="w-24 text-right font-bold"
            data-testid="admin-points-input"
          />
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => updatePoints(points - 5)} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200" data-testid="admin-points-minus">
            <Minus className="w-4 h-4" />
          </button>
          <input type="range" min="1" max={sliderMax} value={Math.min(points, sliderMax)} onChange={(e) => updatePoints(e.target.value)} className="flex-1 accent-blue-600 h-2" data-testid="admin-points-slider" />
          <button type="button" onClick={() => updatePoints(points + 5)} className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200" data-testid="admin-points-plus">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Staffelpreis */}
      <div className="bg-white rounded-lg border p-3 mb-3 grid grid-cols-3 gap-2 text-center">
        {isBonusMode ? (
          <>
            <div>
              <p className="text-xs text-gray-500">Preis</p>
              <p className="text-sm font-bold text-blue-600">CHF {calc.final_price.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">🎁 Staffel-Bonus</p>
              <p className="text-sm font-bold text-amber-600" data-testid="admin-tier-bonus">{tierBonus > 0 ? `+${tierBonus} Pkt (${calc.tier_label})` : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Punkte</p>
              <p className="text-sm font-bold text-green-600">{totalPoints}</p>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-xs text-gray-500">Normalpreis</p>
              <p className={`text-sm ${calc.discount_percent > 0 ? 'line-through text-gray-400' : 'font-bold'}`}>CHF {calc.full_price.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Staffelrabatt</p>
              <p className="text-sm font-bold text-green-600">{calc.discount_percent > 0 ? `-${calc.discount_percent}% (${calc.tier_label})` : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Preis</p>
              <p className="text-sm font-bold text-blue-600">CHF {calc.final_price.toFixed(2)}</p>
            </div>
          </>
        )}
      </div>

      {/* Extras: Bonus-Punkte oder Zusatz-Rabatt */}
      <div className="bg-white rounded-lg border p-3 mb-3">
        <Label className="text-sm font-medium">Extras (optional)</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button
            type="button"
            onClick={() => { setExtraType('bonus'); setExtraValue(''); }}
            className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border-2 text-sm transition ${extraType === 'bonus' ? 'border-amber-500 bg-amber-50 text-amber-700 font-medium' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
            data-testid="extra-type-bonus"
          >
            <Gift className="w-4 h-4" /> Bonus-Punkte (+Anzahl)
          </button>
          <button
            type="button"
            onClick={() => { setExtraType('discount'); setExtraValue(''); }}
            className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border-2 text-sm transition ${extraType === 'discount' ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
            data-testid="extra-type-discount"
          >
            <Percent className="w-4 h-4" /> Zusatz-Rabatt (−%)
          </button>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Input
            type="number"
            min="0"
            max={extraType === 'discount' ? 100 : 9999}
            step="1"
            placeholder="0"
            value={extraValue}
            onChange={(e) => setExtraValue(e.target.value)}
            className="w-24"
            data-testid="extra-percent-input"
          />
          <span className="text-sm text-gray-600">
            {extraType === 'bonus'
              ? `Punkte → +${manualBonus} Punkte geschenkt 🎁`
              : `% → −CHF ${extraDiscountAmount.toFixed(2)} zusätzlich`}
          </span>
        </div>
      </div>

      {/* Zusammenfassung */}
      <div className="bg-blue-600 text-white rounded-lg p-3 mb-3 text-center" data-testid="admin-points-summary">
        <p className="font-bold">
          Kunde erhält {totalPoints} Punkte{bonusPoints > 0 ? ` (${points} gekauft + ${bonusPoints} geschenkt 🎁)` : ''} für CHF {finalPrice.toFixed(2)}
        </p>
      </div>

      <Button onClick={handleContinue} className="w-full" data-testid="admin-points-continue">
        Weiter zur Zahlung <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

export default AdminPointsCalculator;
